import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, 
  TrendingUp, 
  Target, 
  Users, 
  Calendar,
  Mail,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const Home = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5, ease: "easeOut" }
  };

  const packages = [
    {
      name: "Starter Package",
      price: "$1,200",
      period: "/month",
      description: "Perfect for building your content foundation",
      features: [
        "8 social media posts",
        "1 blog article",
        "2 email campaigns",
        "2 educational handouts"
      ],
      color: "accent-purple"
    },
    {
      name: "Growth Package",
      price: "$2,000",
      period: "/month",
      description: "Scale your content and reach more patients",
      features: [
        "16 social media posts",
        "2 blog articles",
        "4 email campaigns",
        "4 educational handouts"
      ],
      color: "accent-blue",
      featured: true
    },
    {
      name: "Authority Package",
      price: "$2,500",
      period: "/month",
      description: "Establish your brand as the industry leader",
      features: [
        "20 social media posts",
        "4 blog articles",
        "4-6 email campaigns",
        "2 educational handouts",
        "Website content updates",
        "Monthly analytics report"
      ],
      color: "accent-orange"
    }
  ];

  const addons = [
    {
      title: "Paid Advertising",
      description: "Meta, LinkedIn, Google Search campaigns",
      setupFee: "$750",
      monthly: "$1,100",
      icon: TrendingUp
    },
    {
      title: "Funnel & Lead Capture",
      description: "Simple, healthcare-appropriate lead capture systems",
      setupFee: "$1,500",
      monthly: "$1,700",
      icon: Target
    }
  ];

  const services = [
    "Content strategy & planning",
    "Short-form content creation",
    "Paid advertising (LinkedIn, Meta, Google)",
    "Funnel & lead capture setup",
    "Brand messaging & positioning"
  ];

  const faqs = [
    {
      question: "What exactly does Praxis Media provide?",
      answer: "We provide professionally written, healthcare-compliant content such as social captions, blog articles, emails, and educational materials. All content is written copy only and delivered ready to post."
    },
    {
      question: "Do you post the content for us?",
      answer: "No. We deliver final written content in Google Docs so you maintain full control over what is published and when. Most clients post the content themselves or hand it to a designer or VA."
    },
    {
      question: "Do you design graphics or visuals?",
      answer: "At this time, we focus on written content only. This allows us to prioritize clarity, accuracy, and compliance."
    },
    {
      question: "How do we review and approve content?",
      answer: "Content is delivered via Google Docs. You can comment directly in the document, request edits, or approve the draft when ready."
    },
    {
      question: "How many revisions are included?",
      answer: "Minor revisions are included with each deliverable. We ask that feedback be consolidated to keep the process efficient."
    },
    {
      question: "Is the content compliant with healthcare regulations?",
      answer: "All content is educational and informational only and avoids diagnosis, treatment recommendations, or medical guarantees reviewed by a tenured registered nurse. Final responsibility for posted content remains with the client."
    },
    {
      question: "How quickly do we receive content?",
      answer: "Content is delivered on a weekly cadence, depending on your selected package."
    },
    {
      question: "What industries do you work with?",
      answer: "We primarily work with: Home health agencies, Med spas and wellness clinics, Private practices, Healthcare service businesses."
    },
    {
      question: "What is the investment?",
      answer: "Monthly retainers typically range from $1,200–$2,500, depending on content volume and scope."
    },
    {
      question: "Is there a long-term contract?",
      answer: "No long-term contracts are required. We start with a 30-day engagement and continue month-to-month."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="home-page">
      {/* Header */}
      <header className="header-nav">
        <div className="container">
          <div className="header-content">
            <div className="logo-text">PRAXIS MEDIA</div>
            <nav className="nav-actions">
              <a href="#services" className="nav-link">Services</a>
              <a href="#packages" className="nav-link">Packages</a>
              <a href="#about" className="nav-link">About</a>
              <a href="#faq" className="nav-link">FAQ</a>
              <a href="#contact" className="btn-primary">Get Started</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <motion.div className="hero-content" {...fadeInUp}>
            <div className="hero-announcement">
              <span>Healthcare Content That Converts</span>
            </div>
            <h1 className="heading-hero">
              We Turn Content Into Clients<br />for Healthcare
            </h1>
            <p className="hero-subtitle body-large">
              Strategy. Execution. Consistency. Conversion.<br />
              Professional content for home health agencies, private practices,<br />
              wellness centers, and healthcare clinics.
            </p>
            <div className="hero-cta-group">
              <a href="#contact" className="btn-primary">Schedule Consultation</a>
              <a href="#packages" className="btn-secondary">View Packages</a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Target Audiences */}
      <section className="audiences-section">
        <div className="container">
          <motion.div className="section-header" {...fadeInUp}>
            <h2 className="heading-1">Who We Serve</h2>
          </motion.div>
          <div className="audience-grid">
            {["Home Health Agencies", "Private Practices", "Wellness Centers", "Healthcare Clinics", "Med Spas"].map((audience, index) => (
              <motion.div 
                key={index}
                className="audience-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Users size={24} />
                <span className="body-medium">{audience}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="services-section">
        <div className="container">
          <motion.div className="section-header" {...fadeInUp}>
            <h2 className="heading-1">Our Services</h2>
            <p className="body-large">Comprehensive content solutions for healthcare brands</p>
          </motion.div>
          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-item"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <CheckCircle2 size={20} className="service-icon" />
                <span className="body-medium">{service}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="packages-section">
        <div className="container">
          <motion.div className="section-header" {...fadeInUp}>
            <h2 className="heading-1">Content Packages</h2>
            <p className="body-large">Choose the package that fits your growth goals</p>
          </motion.div>
          <div className="packages-grid">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                className={`package-card ${pkg.color} ${pkg.featured ? 'featured' : ''}`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                {pkg.featured && <div className="featured-badge">MOST POPULAR</div>}
                <div className="package-header">
                  <h3 className="heading-2">{pkg.name}</h3>
                  <div className="package-price">
                    <span className="price">{pkg.price}</span>
                    <span className="period">{pkg.period}</span>
                  </div>
                  <p className="body-small package-description">{pkg.description}</p>
                </div>
                <div className="package-features">
                  {pkg.features.map((feature, idx) => (
                    <div key={idx} className="feature-item">
                      <CheckCircle2 size={16} />
                      <span className="body-small">{feature}</span>
                    </div>
                  ))}
                </div>
                <a href="#contact" className="btn-primary package-cta">Get Started</a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="addons-section">
        <div className="container">
          <motion.div className="section-header" {...fadeInUp}>
            <h2 className="heading-1">Premium Add-Ons</h2>
            <p className="body-large">Accelerate your growth with advanced marketing services</p>
          </motion.div>
          <div className="addons-grid">
            {addons.map((addon, index) => (
              <motion.div
                key={index}
                className="addon-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="addon-icon">
                  <addon.icon size={32} />
                </div>
                <h3 className="heading-3">{addon.title}</h3>
                <p className="body-small addon-description">{addon.description}</p>
                <div className="addon-pricing">
                  <div className="pricing-item">
                    <span className="label caption">Setup Fee</span>
                    <span className="value body-medium">{addon.setupFee}</span>
                  </div>
                  <div className="pricing-item">
                    <span className="label caption">Monthly</span>
                    <span className="value body-medium">{addon.monthly}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="about-section">
        <div className="container">
          <motion.div className="about-content" {...fadeInUp}>
            <h2 className="heading-1">About Praxis Media</h2>
            <p className="body-large about-intro">
              Praxis Media is a content and advertising agency supporting healthcare and 
              service-based brands with strategy-driven content and paid growth campaigns.
            </p>
            <div className="about-services">
              <h3 className="heading-3">Services include:</h3>
              <ul className="about-list">
                {services.map((service, index) => (
                  <li key={index} className="body-medium">
                    <CheckCircle2 size={18} />
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="body-large about-tagline">
              We focus on <strong>clarity</strong>, <strong>consistency</strong>, and <strong>conversion</strong>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="faq-section">
        <div className="container">
          <motion.div className="section-header" {...fadeInUp}>
            <h2 className="heading-1">Frequently Asked Questions</h2>
          </motion.div>
          <div className="faq-container">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className={`faq-item ${openFAQ === index ? 'open' : ''}`}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <button 
                  className="faq-question"
                  onClick={() => toggleFAQ(index)}
                >
                  <span className="body-medium">{faq.question}</span>
                  {openFAQ === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                <div className={`faq-answer ${openFAQ === index ? 'show' : ''}`}>
                  <p className="body-small">{faq.answer}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contact" className="contact-section">
        <div className="container">
          <motion.div className="contact-content" {...fadeInUp}>
            <h2 className="heading-1">Ready to Turn Content Into Clients?</h2>
            <p className="body-large">
              Let's discuss how we can help you grow your healthcare practice with 
              strategic, compliant content.
            </p>
            <div className="contact-actions">
              <a 
                href="https://calendly.com/praxismedia4/30min" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary contact-btn"
              >
                <Calendar size={20} />
                <span>Schedule Consultation</span>
              </a>
              <a 
                href="mailto:Praxismedia4@gmail.com" 
                className="btn-secondary contact-btn"
              >
                <Mail size={20} />
                <span>Email Us</span>
              </a>
            </div>
            <div className="contact-info">
              <a href="mailto:Praxismedia4@gmail.com" className="contact-link mono-text">
                Praxismedia4@gmail.com
              </a>
              <a href="tel:+19892138982" className="contact-link mono-text">
                (989) 213-8982
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="logo-text">PRAXIS MEDIA</div>
              <p className="body-small footer-tagline">
                Strategy. Execution. Consistency. Conversion.
              </p>
            </div>
            <div className="footer-links">
              <a href="#services" className="footer-link body-small">Services</a>
              <a href="#packages" className="footer-link body-small">Packages</a>
              <a href="#about" className="footer-link body-small">About</a>
              <a href="#faq" className="footer-link body-small">FAQ</a>
            </div>
            <div className="footer-contact">
              <a href="mailto:Praxismedia4@gmail.com" className="body-small">
                Praxismedia4@gmail.com
              </a>
              <a href="tel:+19892138982" className="body-small">
                (989) 213-8982
              </a>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="caption">
              © {new Date().getFullYear()} Praxis Media. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
